import {
  createRpcClient,
  type RpcClient,
} from "@lodestone/hungry-lion-procurement-client";
import { useMemo, type ReactNode } from "react";

import { useAuthSession } from "../auth/logto-auth-session";
import {
  ProcurementRuntimeProvider,
  type CatalogOperations,
  type DemandOperations,
  type ProcurementRuntime,
  type ReceivingOperations,
} from "./procurement-runtime";

export const createRpcCatalogOperations = (
  rpc: RpcClient,
): CatalogOperations => ({
  async list({ kind, locationType, projectID }) {
    if (kind === "Location") {
      return (
        await rpc.call("catalog.locations.list", {
          projectID,
          ...(locationType ? { locationType } : {}),
        })
      ).records;
    }
    if (kind === "SKU") {
      return (await rpc.call("catalog.skus.list", { projectID })).records;
    }
    if (kind === "Category")
      return (await rpc.call("catalog.categories.list", { projectID })).records;
    return (await rpc.call("catalog.suppliers.list", { projectID })).records;
  },

  async listUnitOfMeasures(projectID) {
    return (await rpc.call("catalog.unitOfMeasures.list", { projectID }))
      .values;
  },

  async save(command) {
    const { existing, idempotencyKey, name, projectID } = command;
    if (command.kind === "Location") {
      if (existing) {
        await rpc.call("catalog.locations.update", {
          ID: existing.ID,
          expectedVersion: existing.version,
          idempotencyKey,
          locationType: command.locationType,
          name,
          projectID,
        });
      } else {
        await rpc.call("catalog.locations.create", {
          idempotencyKey,
          locationType: command.locationType,
          name,
          projectID,
        });
      }
      return;
    }
    if (command.kind === "SKU") {
      if (existing) {
        await rpc.call("catalog.skus.update", {
          ID: existing.ID,
          expectedVersion: existing.version,
          idempotencyKey,
          name,
          projectID,
          unitOfMeasure: command.unitOfMeasure,
          categoryID: command.categoryID,
          erpNo: command.erpNo,
          description: command.description,
        });
      } else {
        await rpc.call("catalog.skus.create", {
          idempotencyKey,
          name,
          projectID,
          unitOfMeasure: command.unitOfMeasure,
          categoryID: command.categoryID,
          erpNo: command.erpNo,
          description: command.description,
        });
      }
      return;
    }
    if (command.kind === "Category") {
      if (existing)
        await rpc.call("catalog.categories.update", {
          ID: existing.ID,
          expectedVersion: existing.version,
          idempotencyKey,
          name,
          projectID,
        });
      else
        await rpc.call("catalog.categories.create", {
          idempotencyKey,
          name,
          projectID,
        });
      return;
    }
    if (existing) {
      await rpc.call("catalog.suppliers.update", {
        ID: existing.ID,
        expectedVersion: existing.version,
        idempotencyKey,
        name,
        projectID,
      });
    } else {
      await rpc.call("catalog.suppliers.create", {
        idempotencyKey,
        name,
        projectID,
      });
    }
  },

  async setState({ idempotencyKey, projectID, record, state }) {
    const params = {
      ID: record.ID,
      expectedVersion: record.version,
      idempotencyKey,
      projectID,
    };
    if (record.kind === "Location") {
      await rpc.call(
        state === "Active"
          ? "catalog.locations.reactivate"
          : "catalog.locations.retire",
        params,
      );
    } else if (record.kind === "SKU") {
      await rpc.call(
        state === "Active" ? "catalog.skus.reactivate" : "catalog.skus.retire",
        params,
      );
    } else if (record.kind === "Category") {
      await rpc.call(
        state === "Active"
          ? "catalog.categories.reactivate"
          : "catalog.categories.retire",
        params,
      );
    } else {
      await rpc.call(
        state === "Active"
          ? "catalog.suppliers.reactivate"
          : "catalog.suppliers.retire",
        params,
      );
    }
  },

  async remove({ idempotencyKey, projectID, record }) {
    const params = {
      ID: record.ID,
      expectedVersion: record.version,
      idempotencyKey,
      projectID,
    };
    if (record.kind === "Location") {
      await rpc.call("catalog.locations.delete", params);
    } else if (record.kind === "SKU") {
      await rpc.call("catalog.skus.delete", params);
    } else if (record.kind === "Category") {
      await rpc.call("catalog.categories.delete", params);
    } else {
      await rpc.call("catalog.suppliers.delete", params);
    }
  },
});

export const createRpcDemandOperations = (
  rpc: RpcClient,
): DemandOperations => ({
  async list(projectID) {
    return (await rpc.call("demands.list", { projectID })).demands;
  },
  async listAllocationGroups(projectID) {
    return (await rpc.call("demandTargetAllocationGroups.list", { projectID }))
      .groups;
  },
  renameAllocationGroup(command) {
    return rpc.call("demandTargetAllocationGroups.rename", command);
  },
  activateAllocationGroup(command) {
    return rpc.call("demandTargetAllocationGroups.activate", command);
  },
  updateGroup(command) {
    return rpc.call("demandGroups.update", command);
  },
  renameGroup(command) {
    return rpc.call("demandGroups.rename", command);
  },
  createGroup(command) {
    return rpc.call("demandGroups.create", command);
  },
  activateGroup(command) {
    return rpc.call("demandGroups.activate", command);
  },
  assignTargets(command) {
    return rpc.call("demandGroups.assignTargets", command);
  },
});

export const createRpcReceivingOperations = (
  rpc: RpcClient,
): ReceivingOperations => ({
  async list(projectID, locationID) {
    return (await rpc.call("receiving.list", { projectID, locationID }))
      .allocationGroups;
  },
  record(command) {
    return rpc.call("receiving.record", command);
  },
});

export const ProductionProcurementRuntimeProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const auth = useAuthSession();
  const runtime = useMemo<ProcurementRuntime>(() => {
    const rpc = createRpcClient({ authSession: auth.session });
    return {
      auth,
      catalog: createRpcCatalogOperations(rpc),
      demands: createRpcDemandOperations(rpc),
      receiving: createRpcReceivingOperations(rpc),
    };
  }, [auth]);

  return (
    <ProcurementRuntimeProvider runtime={runtime}>
      {children}
    </ProcurementRuntimeProvider>
  );
};
